import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  pushSupported,
  pushPermission,
  getCurrentSubscription,
  enablePush,
  disablePush,
} from "../lib/push";

export default function PushToggle() {
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [permission, setPermission] = useState("default");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sup = pushSupported();
    setSupported(sup);
    if (!sup) return;
    setPermission(pushPermission());
    getCurrentSubscription().then((s) => setEnabled(!!s));
  }, []);

  if (!supported) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="push-not-supported">
        Le notifiche push non sono supportate da questo browser. Su iPhone installa prima l'app (Aggiungi a Home) per attivarle.
      </div>
    );
  }

  const blocked = permission === "denied";

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        toast.success("Notifiche disattivate");
      } else {
        await enablePush();
        setEnabled(true);
        setPermission("granted");
        toast.success("Notifiche attivate", {
          description: "Riceverai un avviso per offerte, baratti, messaggi e aste",
        });
      }
    } catch (e) {
      toast.error(e.message || "Errore notifiche");
      setPermission(pushPermission());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-4" data-testid="push-toggle-card">
      <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${enabled ? "bg-[#1d3d3a] text-white" : "bg-slate-100 text-slate-500"}`}>
        {enabled ? <Bell className="w-6 h-6" /> : <BellOff className="w-6 h-6" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-black text-[#1d3d3a]">Notifiche push</h3>
          {enabled && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
              Attive
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Ricevi un avviso istantaneo quando arriva un'<strong>offerta</strong>, una <strong>proposta di baratto</strong>,
          un <strong>messaggio</strong> in chat o una <strong>puntata d'asta</strong> — anche con l'app chiusa.
          <span className="block mt-1 text-xs text-slate-500">
            Le notifiche richiedono il tuo consenso esplicito. Dettagli nella{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#e86a24] font-semibold hover:underline">Privacy Policy</a>.
          </span>
        </p>
        {blocked && !enabled ? (
          <p className="mt-2 text-xs text-red-600">
            Le notifiche sono bloccate. Sblocca dal lucchetto/icona accanto all'URL del browser, poi riprova.
          </p>
        ) : null}
        <Button
          onClick={handleToggle}
          disabled={loading || (blocked && !enabled)}
          className={`mt-3 rounded-full ${enabled ? "bg-slate-200 hover:bg-slate-300 text-slate-800" : "bg-[#e86a24] hover:bg-[#c8561a] text-white"}`}
          data-testid="push-toggle-button"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : enabled ? <BellOff className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
          {enabled ? "Disattiva notifiche" : "Attiva notifiche"}
        </Button>
      </div>
    </div>
  );
}
